/**
 * Edit User Form Schema
 * 
 * Defines the validation rules for editing existing users in the system.
 * 
 * @module forms/users/editUser/schema
 * 
 * @remarks
 * - Reuses AddUserFormSchema for validation (same fields)
 * - Loads edit-specific form metadata from form.yml
 * - Follows DRY principles: labels in YAML, validation in Zod
 */

import { FormConfigLoader } from '@/lib/formConfigLoader'
import formYaml from './form.yml?raw'
import { AddUserFormSchema, transformToApiRequest as addUserTransformToApiRequest, type AddUserFormData } from '../addUser/schema'

// Load form configuration from YAML
const formConfig = FormConfigLoader.loadFormConfig(formYaml)

// ============================================================================
// Export Form Metadata from YAML
// ============================================================================

export const FORM_CONFIG = formConfig
export const FORM_NAME = formConfig.formName
export const FORM_TITLE = formConfig.formTitle
export const FORM_DESCRIPTION = formConfig.formDescription

// ============================================================================
// Form Schema (reuses AddUserFormSchema - same fields)
// ============================================================================

export const EditUserFormSchema = AddUserFormSchema
export type EditUserFormData = AddUserFormData

/**
 * Transform form data to API request format for editing
 * 
 * Converts form structure to API structure, preserving user ID and status.
 * 
 * @param formData - Validated form data from EditUserFormSchema
 * @returns API request object ready for backend submission
 * 
 * @remarks
 * - Reuses addUser transform function
 * - User ID and status should be set in composer (not here)
 */
export function transformToApiRequest(formData: EditUserFormData) {
  return addUserTransformToApiRequest(formData)
}
